import { useState, useEffect } from 'react'

/**
 * Custom hook for responsive behavior
 * Provides breakpoint detection and adaptive utilities
 */

const breakpoints = {
  mobile: 320,
  tablet: 768,
  desktop: 1024,
  largeDesktop: 1440,
  ultraWide: 1920
}

export const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Breakpoint checks
  const isMobile = windowSize.width < breakpoints.tablet
  const isTablet = windowSize.width >= breakpoints.tablet && windowSize.width < breakpoints.desktop
  const isDesktop = windowSize.width >= breakpoints.desktop
  const isLargeDesktop = windowSize.width >= breakpoints.largeDesktop
  const isUltraWide = windowSize.width >= breakpoints.ultraWide

  // Device type
  const deviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'

  // Adaptive values based on screen size
  const getAdaptiveValue = (mobileValue, tabletValue, desktopValue) => {
    if (isMobile) return mobileValue
    if (isTablet) return tabletValue || mobileValue
    return desktopValue || tabletValue || mobileValue
  }

  // Grid columns based on screen size
  const getAdaptiveColumns = (items, maxCols = 4) => {
    if (isMobile) return 1
    if (isTablet) return Math.min(2, maxCols)
    if (isDesktop) return Math.min(3, maxCols)
    return Math.min(maxCols, 4)
  }

  // Spacing values
  const getAdaptiveSpacing = (size = 'default') => {
    const spacingMap = {
      sm: getAdaptiveValue('0.5rem', '0.75rem', '1rem'),
      default: getAdaptiveValue('1rem', '1.5rem', '2rem'),
      lg: getAdaptiveValue('1.5rem', '2rem', '3rem'),
      xl: getAdaptiveValue('2rem', '3rem', '4rem')
    }
    return spacingMap[size] || spacingMap.default
  }

  // Font sizes
  const getAdaptiveFontSize = (size = 'base') => {
    const fontSizeMap = {
      xs: getAdaptiveValue('0.75rem', '0.75rem', '0.875rem'),
      sm: getAdaptiveValue('0.875rem', '0.875rem', '1rem'),
      base: getAdaptiveValue('1rem', '1rem', '1.125rem'),
      lg: getAdaptiveValue('1.125rem', '1.25rem', '1.5rem'),
      xl: getAdaptiveValue('1.25rem', '1.5rem', '1.875rem'),
      '2xl': getAdaptiveValue('1.5rem', '1.875rem', '2.25rem'),
      '3xl': getAdaptiveValue('1.875rem', '2.25rem', '3rem'),
      '4xl': getAdaptiveValue('2.25rem', '3rem', '3.75rem')
    }
    return fontSizeMap[size] || fontSizeMap.base
  }

  // Layout configurations
  const getLayoutConfig = () => {
    return {
      sidebar: {
        width: getAdaptiveValue('100%', '280px', '320px'),
        collapsible: !isMobile,
        overlay: isMobile
      },
      modal: {
        fullScreen: isMobile,
        maxWidth: getAdaptiveValue('100%', '600px', '800px')
      },
      card: {
        padding: getAdaptiveValue('1rem', '1.5rem', '2rem'),
        gap: getAdaptiveValue('1rem', '1.5rem', '2rem')
      },
      navigation: {
        type: isMobile ? 'drawer' : 'horizontal',
        showLabels: !isMobile
      }
    }
  }

  // Touch/hover behavior
  const prefersTouchInteraction = isMobile || isTablet
  const prefersHoverInteraction = isDesktop

  // Performance considerations
  const shouldUseReducedMotion = () => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  const shouldLazyLoad = isMobile // More aggressive lazy loading on mobile

  return {
    // Size information
    windowSize,
    deviceType,
    
    // Breakpoint checks
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    isUltraWide,
    
    // Adaptive utilities
    getAdaptiveValue,
    getAdaptiveColumns,
    getAdaptiveSpacing,
    getAdaptiveFontSize,
    getLayoutConfig,
    
    // Interaction preferences
    prefersTouchInteraction,
    prefersHoverInteraction,
    
    // Performance
    shouldUseReducedMotion: shouldUseReducedMotion(),
    shouldLazyLoad,
    
    // Breakpoint values
    breakpoints
  }
}

export default useResponsive